import React, {useEffect, useMemo, useState} from 'react';
import {Button, StatusBadge, Icon, Text} from '@/ui-kits';
import {cn, formatConsultationDate, formatName} from '@/utils/common_functions';
import {ConsultationStatus, SpeclaistConsultationStatusBadgeVariants, SpecialistConsultationStatusLabels, ConsultationTypeLabels} from '@/constants';
import {FileItem, SpecialistConsultation} from '@/interface';
import {FilePreview, ChatButton} from '../../../common'; // avoid self file path resolution
import {DeadlineTimer} from '../../../deadline-timer';

interface SpecialistConsultationCardProps extends SpecialistConsultation {
  onViewPatientDetails: (id: number) => void;
  onViewCase: (id: number) => void;
  onChat?: (id: number, e?: React.MouseEvent<HTMLButtonElement>) => void;
  onViewFeedback?: (id: number) => void;
  onViewReport?: (id: number) => void;
}

export const SpecialistConsultationCard: React.FC<SpecialistConsultationCardProps> = ({
  id,
  patient_name,
  created_at,
  chat_active,
  deadline_at,
  type,
  status,
  onViewPatientDetails,
  onViewCase,
  feedback,
  cancelled_on,
  onViewReport,
  report,
  review_date,
  doctor_files,
  onViewFeedback,
  has_case_opened,
  onChat,
  referral_details,
}) => {
  const isOverdue = status.id === ConsultationStatus.Overdue;
  const showFeedbackButton = useMemo(() => {
    if (status.id === ConsultationStatus.Completed) {
      return Boolean(feedback);
    }
    if (status.id === ConsultationStatus.ReportReady) {
      return true;
    }
    return false;
  }, [status.id, feedback]);

  const actionButtonClass =
    'w-full sm:w-auto sm:min-w-0 sm:flex-shrink text-xs sm:text-sm md:text-xs lg:text-sm px-3 sm:px-4 md:px-3 lg:px-4 py-1.5 sm:py-2 md:py-1.5 lg:py-2 min-[481px]:px-4 min-[481px]:py-1.5';

  const inReview = [ConsultationStatus.AwaitingDoctorReview, ConsultationStatus.UnderReviewByDoctor].includes(status.id);

  return (
    <div className="bg-white rounded-lg shadow-xl border border-ui_input_border_dark/35 p-4 sm:p-6 specialist-consultation-card">
      {/* Case ID and Status */}
      <div className="flex flex-row flex-wrap items-center mb-4 sm:mb-6 border-b border-ui_input_border_dark/30 pb-3 sm:pb-4 gap-3 specialist-consultation-card-header-lg">
        <h2 className="case_id-lg text-lg sm:text-xl md:text-lg lg:text-2xl font-FigtreeRegular text-ui_blue text-nowrap">
          Case ID : <span className="font-FigtreeBold">#{id}</span>
        </h2>

        {referral_details && (
          <Text variant="subtitle" className="referral_details-lg text-ui_input_border_dark !font-FigtreeRegular">
            (Referred to <b className="!font-FigtreeSemiBold">Dr. {formatName(referral_details.specialist_name)}</b> ({referral_details.category.label}))
          </Text>
        )}

        <StatusBadge status={SpeclaistConsultationStatusBadgeVariants[status.id]} className="status_badge-lg ml-auto">
          {SpecialistConsultationStatusLabels[status.id]}
        </StatusBadge>
      </div>

      {referral_details && (
        <div className={cn('specialist-consultation-card-header-sm flex flex-row flex-wrap items-center gap-3')}>
          <h2 className="case_id-sm text-lg sm:text-xl md:text-lg lg:text-2xl font-FigtreeRegular text-ui_blue text-nowrap">
            Case ID : <span className="font-FigtreeBold">#{id}</span>
          </h2>

          {referral_details && (
            <Text variant="subtitle" className="referral_details-sm text-ui_input_border_dark grow !font-FigtreeRegular">
              (Referred to <b className="!font-FigtreeSemiBold">Dr. {formatName(referral_details.specialist_name)}</b> ({referral_details.category.label}))
            </Text>
          )}
          <StatusBadge status={SpeclaistConsultationStatusBadgeVariants[status.id]} className="status_badge-sm ml-auto">
            {SpecialistConsultationStatusLabels[status.id]}
          </StatusBadge>
        </div>
      )}

      {/* Details Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-7 lg:gap-8 mb-4 sm:mb-6">
        <div className="min-w-[120px]">
          <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-2">PATIENT NAME</p>
          <p className="text-sm md:text-sm lg:text-sm xl:text-base 2xl:text-base font-FigtreeBold text-ui_blue leading-tight">{formatName(patient_name)}</p>
        </div>

        <div className="min-w-0">
          <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-2">CONSULTATION BOOKED</p>
          <p className="text-sm md:text-xs lg:text-sm xl:text-base 2xl:text-base font-FigtreeSemiBold text-ui_blue leading-tight">
            {formatConsultationDate(created_at)}
          </p>
        </div>

        <div className="min-w-0">
          <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-2">CONSULTATION TYPE</p>
          <p className="text-sm md:text-xs lg:text-sm xl:text-base 2xl:text-base font-FigtreeSemiBold text-ui_blue leading-tight">
            {ConsultationTypeLabels[type.id]}
          </p>
        </div>

        {![ConsultationStatus.CancelledByPatient, ConsultationStatus.Completed, ConsultationStatus.ReportReady].includes(status.id) && (
          <div className="min-w-0 flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-2">DEADLINE</p>
              <p
                className={`text-sm md:text-xs lg:text-sm xl:text-base 2xl:text-base font-FigtreeSemiBold leading-tight ${
                  isOverdue ? 'text-ui_red' : 'text-ui_blue'
                }`}>
                {formatConsultationDate(deadline_at)}
              </p>
              {!isOverdue && deadline_at && (
                <p className="text-xs font-FigtreeMedium text-ui_button_red mt-0.5">
                  <DeadlineTimer deadline_at={deadline_at} />
                </p>
              )}
            </div>
            <div className="relative group sm:hidden flex-shrink-0 ml-2">
              <div className="p-[10px]">
                {status.id !== ConsultationStatus.AwaitingDoctorReview && chat_active && <ChatButton size="sm" onClick={e => onChat?.(id, e)} chatId={id} />}
              </div>
            </div>
          </div>
        )}

        {status.id === ConsultationStatus.CancelledByPatient && (
          <div className="min-w-0">
            <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-2">CANCELLED ON</p>
            <p className="text-sm md:text-xs lg:text-sm xl:text-base 2xl:text-base font-FigtreeSemiBold text-rejection leading-tight">
              {cancelled_on ? formatConsultationDate(cancelled_on) : 'N/A'}
            </p>
          </div>
        )}

        {[ConsultationStatus.Completed, ConsultationStatus.ReportReady].includes(status.id) && (
          <div className="min-w-0 flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-2">REVIEWED ON</p>
              <p className="text-sm md:text-xs lg:text-sm xl:text-base 2xl:text-base font-FigtreeSemiBold text-ui_blue leading-tight">
                {review_date ? formatConsultationDate(review_date) : 'N/A'}
              </p>
            </div>
            <div className="relative group sm:hidden flex-shrink-0 ml-2">
              <div className="p-[10px]">
                {status.id !== ConsultationStatus.AwaitingDoctorReview && chat_active && <ChatButton size="sm" onClick={e => onChat?.(id, e)} chatId={id} />}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {/* here overflow was changed from hidden to visible since overflow-hidden causde the chat message unread count label to get chopped off*/}
      {status.id !== ConsultationStatus.CancelledByPatient && (
        <div className="specialist-consulation-action-cta-container flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-4 overflow-visible">
          <Button title="View Patient Details" variant="secondary" className={actionButtonClass} onClick={() => onViewPatientDetails(id)} />
          {(inReview || status.id === ConsultationStatus.Overdue) && (
            <Button title={has_case_opened ? 'Edit Case' : 'View Case'} variant="primary" className={actionButtonClass} onClick={() => onViewCase(id)} />
          )}
          {showFeedbackButton && (
            <Button title="View Patient Feedback" variant="secondary" className={actionButtonClass} onClick={() => onViewFeedback?.(id)} />
          )}

          {!inReview && report && (
            <Button
              title="View Submitted Report"
              textClassName="text-nowrap"
              variant="primary"
              className={cn(actionButtonClass, status.id === ConsultationStatus.ReportReady && 'v-sp-sr-lg')}
              onClick={() => onViewReport?.(id)}
            />
          )}

          {status.id !== ConsultationStatus.AwaitingDoctorReview && chat_active && (
            <ChatButton
              className={cn(status.id === ConsultationStatus.UnderReviewByDoctor ? 'hidden sm:flex' : 'c-sp-lg', 'flex-shrink-0')}
              onClick={e => onChat?.(id, e)}
              chatId={id}
            />
          )}
        </div>
      )}

      {status.id !== ConsultationStatus.CancelledByPatient && (
        <div className="flex flex-col sm:flex-row sm:flex-wrap specialist-consulation-action-cta-container sm:mt-4 gap-2 -mt-[2px] overflow-hidden">
          {!inReview && report && (
            <Button
              title="View Submitted Report"
              textClassName="text-nowrap"
              variant="primary"
              className={cn(actionButtonClass, status.id === ConsultationStatus.ReportReady ? 'v-sp-sr-sm' : 'hidden')}
              onClick={() => onViewReport?.(id)}
            />
          )}

          {status.id !== ConsultationStatus.AwaitingDoctorReview && chat_active && (
            <ChatButton
              className={cn(status.id === ConsultationStatus.UnderReviewByDoctor ? 'hidden' : 'c-sp-sm', 'flex-shrink-0')}
              onClick={e => onChat?.(id, e)}
              chatId={id}
            />
          )}
        </div>
      )}

      <DoctorUploadedFilesSection doctor_files={doctor_files || []} status={status.id} />
    </div>
  );
};

const DoctorUploadedFilesSection: React.FC<{
  doctor_files: FileItem[];
  status: ConsultationStatus;
}> = props => {
  const {doctor_files, status} = props;
  const [showFiles, setShowFiles] = useState(false);

  if (!doctor_files) return null;
  if (status !== ConsultationStatus.ReportReady || doctor_files?.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1.5 sm:gap-2 mb-3 sm:mb-5 max-w-full sm:max-w-[75%] mt-4">
      <button onClick={() => setShowFiles(!showFiles)} className="flex items-center gap-4">
        <Text variant="small" className="text-ui_input_border_dark uppercase mb-1 ">
          View Uploaded Files ({doctor_files.length})
        </Text>
        <Icon type={showFiles ? 'cheveron_up' : 'cheveron_down'} className="size-4" />
      </button>

      {/* doctors upload file sections - Show files only when status is awaiting */}
      {showFiles && (
        <div className="flex flex-col gap-1.5 sm:gap-2 mb-3 sm:mb-5 w-full">
          {[ConsultationStatus.ReportReady, ConsultationStatus.Completed].includes(status) &&
            doctor_files.map(file => <FilePreview key={file.id.toString()} bgVariant="blue" allowed_actions={[]} file={file} />)}
        </div>
      )}
    </div>
  );
};
